import { Button, Menu, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  ChooseUploadType,
  Manufacturer,
  ShippingMethod,
  SortBy,
  StatusConst,
  UserOrder,
} from "../interfaces";
import { EFilterBy, TFilter } from "../components/dashboard/Dashboard";
import { MdModeEditOutline } from "react-icons/md";
import { DatePicker } from "@mantine/dates";
import { formatDateAndTime, handleTimeZone } from "../utils";
import TextInlineEdit from "../components/common/TextInlineEdit";
import { Link } from "react-router-dom";
import useOrder from "./useOrder";
import DropDown from "../components/common/DropDown";
import { SHIPPING_METHOD_OPTIONS } from "../constant/DashboardConstant";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

export const STATUS_OPTIONS = [
  { key: StatusConst.Waiting, value: "statusWaiting", color: "#A9A9A9" },
  { key: StatusConst.Error, value: "statusError", color: "#F3DB00" },
  { key: StatusConst.Uploaded, value: "statusUploaded", color: "#0071E3" },
  { key: StatusConst.ReUploaded, value: "statusReUploaded", color: "#00cccc" },
  { key: StatusConst.Production, value: "statusProduction", color: "#34C759" },
  {
    key: StatusConst.ProductionFinished,
    value: "statusProductionFinished",
    color: "#027148",
  },
];

export type TFilterColumns = {
  accessor: string;
  isEditable?: boolean;
  isTooltip?: boolean;
};

type TCellType = {
  column: { isTooltip: boolean; isEditable: boolean };
  value: string;
  row: { original: UserOrder };
};

type TOrderColumnProps = {
  orders: UserOrder[] | null;
  setOrders?: (value: UserOrder[] | null) => void;
  setOrderFilter?: (value: TFilter) => void;
  orderFilter: TFilter | null;
  manufacturers: Manufacturer[];
  onUpdateOrder?: (value: UserOrder) => void;
};

const useOrderColumn = ({
  orders,
  setOrders = () => {},
  setOrderFilter = () => {},
  orderFilter,
  manufacturers,
  onUpdateOrder = () => {},
}: TOrderColumnProps) => {
  const { t } = useTranslation();
  const { handleOrderUpdate } = useOrder();

  /**
   * This method is used to update order state locally
   * @param { Partial<UserOrder> & { id: string } } order order details that need to be updated in local state
   * @returns void
   */
  const handleOrderStateUpdate = (
    order: Partial<UserOrder> & { id: string },
  ) => {
    if (!orders) return;
    setOrders(
      orders.map((_order) => {
        if (_order.id === order.id) return { ..._order, ...order };
        return _order;
      }),
    );
  };

  /**
   * This method is used to update status of order
   * @param { StatusConst } value changed status value
   * @param { string } id order id
   * @returns void
   */
  const onStatusChange = async (value: StatusConst, id: string) => {
    const success = await handleOrderUpdate(
      { id, status: value },
      `Status changed to ${value} for order ${id}`,
    );
    if (!success) return;
    onUpdateOrder(success);
    handleOrderStateUpdate({ id, status: value });
  };

  /**
   * This method is used to update manufacturer of order
   * @param { string } value changed manufacturer value
   * @param { string } id order id
   * @returns void
   */
  const onManufacturerChange = async (value: string, id: string) => {
    const success = await handleOrderUpdate(
      { id, printManufacturer: value },
      `Manufacturer changed to ${value} for order ${id}`,
    );
    if (!success) return;
    onUpdateOrder(success);
    handleOrderStateUpdate({ id, printManufacturer: value });
  };

  /**
   * This method is used to update delivery date of order
   * @param { Date | null } value changed delivery date value
   * @param { string } id order id
   * @returns void
   */
  const onDeliveryDateChange = async (value: Date | null, id: string) => {
    const successMessage = value
      ? `Delivery date changed to ${formatDateAndTime(
          value.toISOString(),
        )} for order ${id}`
      : `Delivery date removed for order ${id}`;

    let deliveryDate = value;
    if (value) {
      // handle timezone format
      deliveryDate = new Date(handleTimeZone(value, true));
    }
    const success = await handleOrderUpdate(
      { id, deliveryDate: deliveryDate ?? null },
      successMessage,
    );
    if (!success) return;
    onUpdateOrder(success);
    handleOrderStateUpdate({ id, deliveryDate: deliveryDate ?? "" });
  };

  /**
   * This method is used to update internal note for order
   * @param { string } value changed internal note value
   * @param { string } id order id
   * @returns void
   */
  const onChangeInternalNote = async (value: string, id: string) => {
    const success = await handleOrderUpdate(
      { id, internalNote: value },
      `Internal note updated for order ${id}.`,
    );
    if (!success) return;
    onUpdateOrder(success);
    handleOrderStateUpdate({ id, internalNote: value });
  };

  /**
   * This method is used to update manufacturer note for order
   * @param { string } value changed manufacturer note value
   * @param { string } id order id
   * @returns void
   */
  const onChangeManufacturerNote = async (value: string, id: string) => {
    const success = await handleOrderUpdate(
      { id, manufacturerNote: value },
      `Manufacturer note updated for order ${id}.`,
    );
    if (!success) return;
    onUpdateOrder(success);
    handleOrderStateUpdate({ id, manufacturerNote: value });
  };

  /**
   * This method is used to update shipping method of order
   * @param { string } value changed shipping method value
   * @param { string } id order id
   * @returns void
   */
  const onChangeShippingMethod = async (value: ShippingMethod, id: string) => {
    const success = await handleOrderUpdate(
      { id, shippingMethod: value },
      `Shipping method updated for order ${id}.`,
    );
    if (!success) return;
    onUpdateOrder(success);
    handleOrderStateUpdate({ id, shippingMethod: value });
  };

  const sortingIcon = () => {
    if (!orderFilter) return <></>;
    if (orderFilter.sortByOrder === null) {
      return <></>;
    } else if (orderFilter.sortByOrder === SortBy.ASC) {
      return <IoIosArrowDown size={30} />;
    } else {
      return <IoIosArrowUp size={30} />;
    }
  };

  const columns = [
    {
      accessor: "id",
      sortValue: "byOrderId",
      Header: (
        <div className="row row-v-center gap-4  white-space-no-wrap">
          {t("orderNumber")}
          {orderFilter?.filterBy === EFilterBy.byOrderId && sortingIcon()}
        </div>
      ),
      Footer: <div className="white-space-no-wrap">{t("orderNumber")}</div>,
      sortType: () => {
        if (!orderFilter) return;
        setOrders([]);
        setOrderFilter({
          ...orderFilter,
          filterBy: EFilterBy.byOrderId,
          sortByOrder:
            orderFilter.sortByOrder === null
              ? SortBy.ASC
              : orderFilter.sortByOrder === SortBy.ASC
                ? SortBy.DESC
                : null,
          nextToken: null,
        });
      },
      Cell: (cell: TCellType) => {
        const order = cell.row.original;
        return (
          <Link
            to={`/dashboard/${
              order.chooseUpload === ChooseUploadType.bulkUpload
                ? "vieworder"
                : "order"
            }/${order.id}`}
          >
            <Text className="table-link">{order.id}</Text>
          </Link>
        );
      },
    },
    {
      accessor: "status",
      sortValue: "byStatus",
      Header: (
        <div className="row row-v-center gap-4  white-space-no-wrap">
          {t("status")}
          {orderFilter?.filterBy === EFilterBy.byStatus && sortingIcon()}
        </div>
      ),
      Footer: <div className="white-space-no-wrap">{t("status")}</div>,
      sortType: () => {
        if (!orderFilter) return;
        if (!orderFilter.filter.status) {
          setOrders([]);
          setOrderFilter({
            ...orderFilter,
            filterBy: EFilterBy.byStatus,
            sortByOrder:
              orderFilter.sortByOrder === null
                ? SortBy.ASC
                : orderFilter.sortByOrder === SortBy.ASC
                  ? SortBy.DESC
                  : null,
            nextToken: null,
          });
        }
      },
      isEditable: true,
      Cell: (cell: TCellType) => {
        const order = cell.row.original;
        return (
          <Menu disabled={!cell.column.isEditable} shadow="md" width={200}>
            <Menu.Target>
              <Button
                miw={140}
                maw={260}
                style={{
                  backgroundColor:
                    STATUS_OPTIONS.find((status) => status.key === cell.value)
                      ?.color ?? "#0071E3", // Uploaded
                }}
                rightIcon={
                  cell.column.isEditable ? <MdModeEditOutline /> : <></>
                }
              >
                {t(
                  STATUS_OPTIONS.find((status) => cell.value === status.key)
                    ?.value ?? "statusUploaded",
                )}
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              {STATUS_OPTIONS.map((status) => (
                <Menu.Item
                  key={status.key}
                  disabled={status.key === cell.value}
                  onClick={() => onStatusChange(status.key, order.id)}
                >
                  {t(status.value ?? "statusUploaded")}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        );
      },
    },
    {
      accessor: "printManufacturer",
      sortValue: "byPrintManufacturer",
      Header: (
        <div className="row row-v-center gap-4  white-space-no-wrap">
          {t("manufacturerId")}
          {orderFilter?.filterBy === EFilterBy.byPrintManufacturer &&
            sortingIcon()}
        </div>
      ),
      Footer: <div className="white-space-no-wrap">{t("manufacturerId")}</div>,
      isEditable: true,
      sortType: () => {
        if (!orderFilter) return;
        if (!orderFilter.filter.printManufacturer) {
          setOrders([]);
          setOrderFilter({
            ...orderFilter,
            filterBy: EFilterBy.byPrintManufacturer,
            sortByOrder:
              orderFilter.sortByOrder === null
                ? SortBy.ASC
                : orderFilter.sortByOrder === SortBy.ASC
                  ? SortBy.DESC
                  : null,
            nextToken: null,
          });
        }
      },
      Cell: (cell: TCellType) => {
        const order = cell.row.original;
        return (
          <>
            {cell.column.isEditable ? (
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <Button
                    color="gray"
                    miw={140}
                    maw={200}
                    rightIcon={<MdModeEditOutline />}
                  >
                    {cell.value}
                  </Button>
                </Menu.Target>

                <Menu.Dropdown>
                  {manufacturers?.map(({ id, name }) => (
                    <Menu.Item
                      key={id}
                      disabled={id === cell.value}
                      onClick={() => onManufacturerChange(id, order.id)}
                    >
                      {name}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            ) : (
              <div>{cell.value}</div>
            )}
          </>
        );
      },
    },
    {
      accessor: "deliveryDate",
      sortValue: "byDeliveryDate",
      Header: (
        <div className="row row-v-center gap-4  white-space-no-wrap">
          {t("deliveryDate")}
          {orderFilter?.filterBy === EFilterBy.byDeliveryDate && sortingIcon()}
        </div>
      ),
      Footer: <div className="white-space-no-wrap">{t("deliveryDate")}</div>,
      isEditable: true,
      sortType: () => {
        if (!orderFilter) return;
        setOrders([]);
        setOrderFilter({
          ...orderFilter,
          filterBy: EFilterBy.byDeliveryDate,
          sortByOrder:
            orderFilter.sortByOrder === null
              ? SortBy.ASC
              : orderFilter.sortByOrder === SortBy.ASC
                ? SortBy.DESC
                : null,
          nextToken: null,
        });
      },
      Cell: (cell: TCellType) => {
        const order = cell.row.original;
        // handle timezone format
        let deliveryDate = cell.value;
        if (cell.value) {
          const cellValueDate = new Date(cell.value);
          deliveryDate = handleTimeZone(cellValueDate, false);
        }
        return (
          <>
            {cell.column.isEditable ? (
              <div className="row row-v-center">
                <DatePicker
                  icon={<MdModeEditOutline />}
                  size="xs"
                  inputFormat="DD.MM.YYYY"
                  width={100}
                  onChange={(value) => onDeliveryDateChange(value, order.id)}
                  defaultValue={deliveryDate ? new Date(deliveryDate) : null}
                />
              </div>
            ) : (
              <div>{formatDateAndTime(deliveryDate)}</div>
            )}
          </>
        );
      },
    },
    {
      accessor: "createdAt",
      sortValue: "byDate",
      Header: (
        <div className="row row-v-center gap-4  white-space-no-wrap">
          {t("orderDate")}
          {orderFilter?.filterBy === EFilterBy.byDate && sortingIcon()}
        </div>
      ),
      Footer: <div className="white-space-no-wrap">{t("orderDate")}</div>,
      sortType: () => {
        if (!orderFilter) return;
        setOrders([]);
        setOrderFilter({
          ...orderFilter,
          filterBy: EFilterBy.byDate,
          sortByOrder:
            orderFilter.sortByOrder === null
              ? SortBy.ASC
              : orderFilter.sortByOrder === SortBy.ASC
                ? SortBy.DESC
                : null,
          nextToken: null,
        });
      },
      Cell: (cell: TCellType) => (
        <div className="white-space-no-wrap">
          {formatDateAndTime(cell.value)}
        </div>
      ),
    },
    {
      accessor: "uploadedDate",
      sortValue: "byUploadedDate",
      Header: (
        <div className="row row-v-center gap-4  white-space-no-wrap">
          {t("uploadDate")}
          {orderFilter?.filterBy === EFilterBy.byUploadedDate && sortingIcon()}
        </div>
      ),
      Footer: <div className="white-space-no-wrap">{t("uploadDate")}</div>,
      sortType: () => {
        if (!orderFilter) return;
        setOrders([]);
        setOrderFilter({
          ...orderFilter,
          filterBy: EFilterBy.byUploadedDate,
          sortByOrder:
            orderFilter.sortByOrder === null
              ? SortBy.ASC
              : orderFilter.sortByOrder === SortBy.ASC
                ? SortBy.DESC
                : null,
          nextToken: null,
        });
      },
      Cell: (cell: TCellType) => (
        <div className="white-space-no-wrap">
          {formatDateAndTime(cell.value, "DD.MM.YYYY HH:mm:ss")}
        </div>
      ),
    },
    {
      accessor: "shippingMethod",
      sortValue: "byShippingMethod",
      Header: <div className="white-space-no-wrap">{t("shippingMethod")}</div>,
      Footer: <div className="white-space-no-wrap">{t("shippingMethod")}</div>,
      isEditable: false,
      Cell: (cell: TCellType) => {
        const order = cell.row.original;
        return (
          <>
            {cell.column.isEditable ? (
              <DropDown
                options={SHIPPING_METHOD_OPTIONS}
                onSelect={(method) =>
                  onChangeShippingMethod(
                    SHIPPING_METHOD_OPTIONS.find((val) => val.key === method)
                      ?.key ?? ShippingMethod.Express,
                    order.id,
                  )
                }
                value={order.shippingMethod ?? ""}
                rightIcon={<MdModeEditOutline />}
              />
            ) : (
              <div>{cell.value}</div>
            )}
          </>
        );
      },
    },
    {
      accessor: "productType",
      sortValue: "byProductType",
      Header: <div className="white-space-no-wrap">{t("productType")}</div>,
      Footer: <div className="white-space-no-wrap">{t("productType")}</div>,
      Cell: (cell: TCellType) => {
        const order = cell.row.original;
        const productType = new Set(
          order.positions.map((position) => position.productGroup),
        );
        return (
          <div className="white-space-no-wrap">
            {Array.from(productType).join(",")}
          </div>
        );
      },
    },
    {
      accessor: "internalNote",
      sortValue: "byInternalNote",
      Header: <div className="white-space-no-wrap">{t("internalNote")}</div>,
      Footer: <div className="white-space-no-wrap">{t("internalNote")}</div>,
      isTooltip: true,
      isEditable: true,
      Cell: (cell: TCellType) => {
        const order = cell.row.original;
        return (
          <TextInlineEdit
            text={cell.value}
            isTooltip={cell.column.isTooltip}
            isEditable={cell.column.isEditable}
            onChange={(note: string) => onChangeInternalNote(note, order.id)}
          />
        );
      },
    },
    {
      accessor: "manufacturerNote",
      sortValue: "byManufacturerNote",
      Header: (
        <div className="white-space-no-wrap">{t("manufacturerNote")}</div>
      ),
      Footer: (
        <div className="white-space-no-wrap">{t("manufacturerNote")}</div>
      ),
      isTooltip: true,
      isEditable: true,
      Cell: (cell: TCellType) => {
        const order = cell.row.original;
        return (
          <TextInlineEdit
            text={cell.value}
            isTooltip={cell.column.isTooltip}
            isEditable={cell.column.isEditable}
            onChange={(note: string) =>
              onChangeManufacturerNote(note, order.id)
            }
          />
        );
      },
    },
    {
      accessor: "lastOpened",
      sortValue: "byLastOpened",
      Header: <div className="white-space-no-wrap">{t("lastOpened")}</div>,
      Footer: <div className="white-space-no-wrap">{t("lastOpened")}</div>,
      isTooltip: true,
      isEditable: false,
      Cell: ({ cell }: { cell: TCellType }) => {
        return (
          <TextInlineEdit
            text={cell.value}
            isTooltip={cell.column.isTooltip}
            isEditable={cell.column.isEditable}
          />
        );
      },
    },
    {
      accessor: "viewOrder",
      Cell: ({ cell }: { cell: TCellType }) => {
        const order = cell.row.original;
        return (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Link
              to={`/dashboard/${
                order.chooseUpload === ChooseUploadType.bulkUpload
                  ? "vieworder"
                  : "order"
              }/${order.id}`}
            >
              <Button miw={130} maw={160}>
                {t("openOrder")}
              </Button>
            </Link>
          </div>
        );
      },
    },
  ];

  /**
   * This method is used to filter columns by input neededColumns
   * @param { TFilterColumns[] } neededColumns needed columns with type TFilterColumns
   * @returns Filtered columns
   */
  const filterColumns = (neededColumns: TFilterColumns[]) => {
    return [
      ...columns
        .map((column) => {
          const selectedColumns = neededColumns.find(
            (nc) => nc.accessor === column.accessor,
          );
          if (!selectedColumns) return;
          column.isEditable = selectedColumns.isEditable;
          column.isTooltip = selectedColumns.isTooltip;
          return column;
        })
        .filter((f) => f),
    ];
  };

  return { columns, filterColumns };
};

export default useOrderColumn;
